import {
  useConnectAccountToEasyPV,
  useDisconnectAccountFromEasyPV,
} from '@/api/mutations/integrations/easyPV';
import useGetMyAccountIntegrations from '@/api/queries/me/useGetMyAccountIntegrations';
import { usePermissions } from '@/hooks/usePermissions';
import {
  ConnectionControl,
  ConnectionControlBenefitList,
} from '@/ui/components';
import { registerFieldLegacyBuilder } from '@/utils/zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Modal,
  RawInput,
  useToastContext,
} from '@payaca/ambrosia';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import EasyPVLogo from './easyPV.svg';

const formSchema = z.object({
  apiKey: z.string().min(1),
});
type FormSchema = z.infer<typeof formSchema>;

export const EasyPVConnectionControl: FC = () => {
  const [isSettingUpConnection, setIsSettingUpConnection] = useState(false);
  const disconnectAccountFromEasyPV = useDisconnectAccountFromEasyPV();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetMyAccountIntegrations();
  const hasConnection = integrations.some(
    (accountIntegration) => accountIntegration.service === 'EASY_PV'
  );
  const isLoading = isLoadingIntegrations;
  const { userHasRequiredPermission } = usePermissions();

  const canUpdate = useMemo(() => {
    return userHasRequiredPermission([
      AccountsPermissions.UPDATE_DESIGN_INTEGRATION,
    ]);
  }, [userHasRequiredPermission]);

  return (
    <ConnectionControl
      allowConnectDisconnect={canUpdate && !isLoading}
      connectionName="Easy PV"
      isConnected={hasConnection}
      onAddConnection={() => setIsSettingUpConnection(true)}
      onRemoveConnection={() => disconnectAccountFromEasyPV.mutateAsync({})}
      logo={EasyPVLogo}
    >
      {hasConnection ? (
        <p>Your Easy PV account has successfully been connected to Payaca.</p>
      ) : (
        <>
          Connect Payaca with Easy PV for easy syncing of your projects.
          <ConnectionControlBenefitList
            benefits={[
              'Create an Easy PV project from Payaca.',
              'Sync your files from Easy PV into Payaca.',
            ]}
          />
        </>
      )}
      <Modal
        isOpen={isSettingUpConnection}
        onClose={() => setIsSettingUpConnection(false)}
        title="Connect your Easy PV account"
      >
        <EasyPVConnectionForm
          onSuccess={() => setIsSettingUpConnection(false)}
        />
      </Modal>
    </ConnectionControl>
  );
};

const EasyPVConnectionForm: FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { mutateAsync: connectAccountToEasyPV } = useConnectAccountToEasyPV();
  const { pushSuccess } = useToastContext();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    control,
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const registerField = registerFieldLegacyBuilder(errors);
  const onSubmit = async (data: FormSchema) => {
    const response = await connectAccountToEasyPV(data);
    switch (response.connectAccountToEasyPV.__typename) {
      case 'ConnectAccountToEasyPVSuccessResponse': {
        pushSuccess('Your Easy PV account has been successfully connected.');
        return onSuccess();
      }
      case 'ConnectAccountToEasyPVInvalidAPIKeyResponse': {
        return setError('root', {
          type: 'server',
          message:
            'The API key you entered appears to be invalid. Please check your Easy PV account and try again.',
        });
      }
      case 'ConnectAccountToEasyPVErrorResponse': {
        return setError('root', {
          type: 'server',
          message: 'Sorry, we were unable to add your Easy PV account.',
        });
      }
      default: {
        return setError('root', {
          type: 'server',
          message: 'An unknown error occurred, please try again later.',
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Body>
        <p className="mb-4">
          Please enter the API key that Easy PV has provided for you. If you do
          not already have one,{' '}
          <a
            href="https://help.easy-pv.co.uk/books/developer-guidelines/page/getting-started"
            target="_blank"
            rel="noreferrer"
          >
            this guide
          </a>{' '}
          should help you get started.
        </p>
        <div className="space-y-2.5">
          <Field.Legacy {...registerField('apiKey')}>
            <Field.Label>API key</Field.Label>
            <RawInput
              {...register('apiKey')}
              placeholder="Your Easy PV API key"
              autoComplete="off"
              onChange={(change) => {
                clearErrors('root');
                void register('apiKey').onChange(change);
              }}
            />
          </Field.Legacy>
          <Field.Legacy {...registerField('root')} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button type="submit" isProcessing={isSubmitting}>
            Connect
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </form>
  );
};
